// A plugin's viewpoint camera, turned into a camera this viewer can be put at.
//
// Owner, 2026-10-21: "this has to work on viewpoints, and the
// elements/fragments in that view point + a screen shot need to be both sent
// to bidlight, so workers can rotate the 3d and see the picture on the view".
//
// THE FRAME MATHS LIVES HERE AND NOWHERE ELSE. The constants come from
// webgl/js/loader/streaming/documentTransformMath.js, which is where the
// per-file placement overrides already do this conversion - importing them
// rather than re-typing the rotation is the whole point: two copies of a
// change-of-basis is how a model and a camera end up in different places.
//
//   Revit frame : X east, Y north, Z UP, project export units
//   Scene frame : glTF - Y UP, FEET
//   Mapping     : Revit (x, y, z) -> scene (x, z, -y)
//
// WE DO NOT RETURN A camera-controls SERIALISATION. setLookAt(px,py,pz,
// tx,ty,tz, transition) is that library's stable public API; its toJSON shape
// is an internal detail that can change under a version bump, and a camera
// state it rejects fails SILENTLY into "could not be restored".
import { Vector3 } from 'three';
import { REVIT_TO_GLTF, FEET_PER_METRE } from '../webgl/js/loader/streaming/documentTransformMath';

// A stored camera says which frame and which units it is in; anything else is
// refused rather than guessed, because a camera in Revit coordinates and one
// already in scene coordinates are the same six numbers.
const KNOWN_FRAMES = ['revit', 'scene'];
const KNOWN_UNITS = ['feet', 'metric'];

const isVec3 = (v) => Array.isArray(v) && v.length === 3 && v.every((n) => Number.isFinite(Number(n)));

/** Metric cameras arrive in metres; the scene is in feet. */
const scaleToFeet = (v, units) => (
  units === 'metric' ? v.map((n) => Number(n) * FEET_PER_METRE) : v.map(Number)
);

/**
 * One point, Revit frame -> scene frame. A DIRECTION (like `up`) must not be
 * translated, only rotated - but REVIT_TO_GLTF is a pure rotation, so the same
 * call is right for both, and a placement override's translation is applied
 * only to the points.
 */
function toScenePoint(v, units) {
  const out = new Vector3(...scaleToFeet(v, units));
  return out.applyMatrix4(REVIT_TO_GLTF);
}

/**
 * The camera to put the viewer at, or null when the stored camera cannot be
 * trusted. Null is a real answer: the issue then opens wherever it would have
 * opened before, which is the behaviour everybody already knows.
 *
 * @param camera  issue.sourceModelViewer.camera as stored
 * @param transform  optional per-file placement override (a THREE.Matrix4
 *        ALREADY in the scene frame, exactly as the geometry gets it). When a
 *        file has been moved on the project card, its geometry moved and its
 *        camera has to move with it or the viewpoint points at where the model
 *        used to be.
 */
export function viewerCameraFrom(camera, { transform = null } = {}) {
  const c = camera && typeof camera === 'object' ? camera : null;
  if (!c) return null;
  if (!isVec3(c.position) || !isVec3(c.target)) return null;

  const frame = String(c.frame || '').toLowerCase();
  const units = String(c.units || '').toLowerCase();
  if (!KNOWN_FRAMES.includes(frame) || !KNOWN_UNITS.includes(units)) return null;

  let position;
  let target;
  let up = null;
  if (frame === 'revit') {
    position = toScenePoint(c.position, units);
    target = toScenePoint(c.target, units);
    if (isVec3(c.up)) up = toScenePoint(c.up, units).normalize();
  } else {
    // Already in the scene frame; units still apply.
    position = new Vector3(...scaleToFeet(c.position, units));
    target = new Vector3(...scaleToFeet(c.target, units));
    if (isVec3(c.up)) up = new Vector3(...scaleToFeet(c.up, units)).normalize();
  }

  if (transform) {
    position.applyMatrix4(transform);
    target.applyMatrix4(transform);
    // `up` is a direction: rotate it with the override but never translate it.
    if (up) {
      const o = new Vector3(0, 0, 0).applyMatrix4(transform);
      up = up.applyMatrix4(transform).sub(o).normalize();
    }
  }

  // A camera looking at its own position has no direction and would hand
  // setLookAt a NaN basis - the viewer then shows nothing, with no error.
  if (position.distanceTo(target) < 1e-6) return null;
  if (![position, target].every((v) => Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z))) return null;

  const fov = Number(c.fov);
  return {
    position: [position.x, position.y, position.z],
    target: [target.x, target.y, target.z],
    ...(up && Number.isFinite(up.x) ? { up: [up.x, up.y, up.z] } : {}),
    fov: Number.isFinite(fov) && fov > 0 && fov < 180 ? fov : 0,
    ortho: c.ortho === true,
    orthoHeight: Number.isFinite(Number(c.orthoHeight)) ? Number(c.orthoHeight) : 0,
    documentName: String(c.documentName || ''),
    documentGuid: String(c.documentGuid || ''),
  };
}

/**
 * Put the viewer at that camera. Separated from the maths so the conversion
 * can be tested without a viewer, which is the only way this gets tested at
 * all - a wrong rotation is invisible in a unit test of "did we call it".
 *
 * ORTHOGRAPHIC VIEWPOINTS ARE NOT FRAMED, ONLY POINTED. This viewer's camera
 * is perspective; there is no orthographic mode to switch into. So an ortho
 * viewpoint is restored as the same eye and the same direction - which is the
 * right place and the wrong projection - and the caller is TOLD so it can say
 * so rather than quietly implying the view is exact.
 *
 * @returns { applied:boolean, approximated:boolean, reason:string }
 */
export function applyViewerCamera(cameraControls, view, { transition = true } = {}) {
  if (!cameraControls || !view) return { applied: false, approximated: false, reason: 'no camera' };
  try {
    const [px, py, pz] = view.position;
    const [tx, ty, tz] = view.target;
    cameraControls.setLookAt(px, py, pz, tx, ty, tz, transition);
    return {
      applied: true,
      approximated: !!view.ortho,
      reason: view.ortho ? 'The viewpoint is orthographic; the 3D view is perspective, so the angle matches and the framing is approximate.' : '',
    };
  } catch (e) {
    return { applied: false, approximated: false, reason: (e && e.message) || 'could not be restored' };
  }
}
